import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Pagination,
  Paper,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table as TableUI,
  Typography
} from '@mui/material';
import {
  CalendarBlank,
  Gear,
  GlobeHemisphereEast,
  Info,
  Sticker,
  Table,
} from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useEdges, useNodes, useReactFlow } from 'reactflow';
import { useFlowLogs } from '../../hooks/useFlowLogs';
import { FlowLogsService } from '../../service/FlowLogsService';
import { FlowService } from '../../service/FlowService';
import { ScheduleService } from '../../service/ScheduleService';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { Session, useSessionStore } from '../../store/storeSessions';
import { NodeProps } from '../../util/Types/NodeProps';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { createSystemTimestamp } from '../../util/dateUtil';
import { findConnectedNodes } from '../../util/executeNode/findConnectedNodes';
import { isActiveEdge } from '../../util/findActiveHandle';
import AutoTriggerTimerModal from '../CoreNodeDIsplay/AutoTriggerTimerModal';
import SettingsModal from '../FlowGlobalNode/SettingsModal/SettingsModal';
import OutputHandle from '../UI/Handle/OutputHandle';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import KitModal from '../UI/KitModal';
import Label from '../UI/Label/Label';
import { HandleId } from '../UI/Handle/types.d';

function updateOutputNode(nd: any) {
  return {
    ...nd,
    data: {
      ...nd.data,
      canSendMail: true,
    },
  };
}

function updateFluxObjectNode(
  nd: any,
  activeSession: Session,
  inputNode: any,
  combinedText: string,
  onUpdateChat: (data: any, fluxId: string) => Promise<void>,
) {
  const entries = nd?.data?.entries || [];

  const findUserSession = nd?.data?.userSessionsChat?.find(
    (session: any) => session.id === activeSession.id,
  );

  if (findUserSession) {
    const updatedSessions = nd.data.userSessionsChat.map((session: any) => {
      if (session.id !== activeSession.id) return session;

      return {
        ...session,
        entries: [
          ...session.entries,
          {
            role: 'user',
            content: inputNode?.data?.text,
            timestamp: createSystemTimestamp(),
          },
          {
            role: 'assistant',
            content: combinedText,
            timestamp: createSystemTimestamp(),
          },
        ],
      };
    });

    onUpdateChat(
      {
        userSessionsChat: updatedSessions,
      },
      nd.id,
    );

    return {
      ...nd,
      data: {
        ...nd.data,
        userSessionsChat: updatedSessions,
      },
    };
  }

  const newEntries = [
    ...entries,
    {
      role: 'user',
      content: inputNode?.data?.text,
      timestamp: createSystemTimestamp(),
    },
    {
      role: 'assistant',
      content: combinedText,
      timestamp: createSystemTimestamp(),
    },
  ];

  onUpdateChat({ entries: newEntries }, nd.id);

  return {
    ...nd,
    data: {
      ...nd.data,
      entries: newEntries,
    },
  };
}

function updateCurrentNode(nd: any) {
  return {
    ...nd,
    data: {
      ...nd.data,
      hasFinished: false,
      loading: false,
    },
  };
}

function updateDefaultNode(nd: any) {
  return {
    ...nd,
    data: {
      ...nd.data,
      flowTrigger: null,
    },
  };
}

function FlowGlobalNode({ id, data }: NodeProps) {
  const { folders } = useAuthStore(state => state);
  const { flowId } = useParams();
  const activeFolder =
    folders?.find(folder => {
      const activeFolderByFlowId = folder?.flows?.find(
        flow => flow?.id === flowId,
      );
      return activeFolderByFlowId;
    }) ?? null;
  const edges = useEdges();
  const nodes: any = useNodes();

  const reactFlow = useReactFlow();
  const { user } = useAuthStore(state => state);
  const { allIds } = findConnectedNodes(reactFlow.getNodes(), edges, id);

  const activeFlow = activeFolder?.flows?.find(flow => flow?.id === flowId);
  const setNotification = useNotificationStore(state => state.setNotification);
  const {
    setNodeState,
    setSaving,
    setSchema,
    setIsPublic,
    isPublic,
    spaceId,
    setIsWebhook,
    isWebhook,
  } = useFlowsStore(state => state);

  const { activeSession, createSession } =
    useSessionStore(state => state);

  const [isAutoTriggerModalOpen, setIsAutoTriggerModalOpen] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const { data: logsData, isLoading: logsLoading } = useFlowLogs(flowId || '', logsPage, 25);
  
  const queryClient = useQueryClient();
  const logCreatedRef = useRef(false);
  
  const createLogMutation = useMutation({
    mutationFn: FlowLogsService.createLog,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flow-logs', flowId] });
    },
  });

  const [formData, setFormData] = useState({
    notesExpanded: false,
    notes: '',
    loading: false,
    isAutotrigger: false,
    triggerMessage: '',
    generateSessionId: false,
    selectedNode: data?.selectedNode || 'none',
    showEmailCalling: data?.showEmailCalling || false,
    showApiCalling: data?.showApiCalling || false,
    summary: data?.summary || '',
  });
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);

  const [intervalData, setIntervalData] = useState(
    data?.intervalData ?? {
      type: 'hourly',
      hourly: '*',
      daily: '*',
      weekly: '*',
      monthly: '*',
      codedTime: '',
      inputText: '',
      varInput: '',
    },
  );

  async function executeFlow() {
    nodeDataAutoSaveDynamic({
      setNodeState,
      id,
      flowTriggerData: {
        flowGlobalId: id,
        flowId: flowId,
      },
      setSaving,
      paused: false,
      changeType: 'execute',
      objectCallerData: [
        ...(user?.spaceObjects || []),
        ...(user?.objects || []),
      ],

      setSchema,
      newEdges: edges,
    });

    setFormData(previousFormData => ({
      ...previousFormData,
      loading: true,
    }));
  }

  const onChange = (e: any) => {
    const { name, value } = e.target;
    let object = {};

    if (name === 'selectedNode') {
      object = { selectedNode: value };
    } else if (name === 'showEmailCalling') {
      object = { showEmailCalling: !data?.showEmailCalling };
    } else if (name === 'showApiCalling') {
      object = { showApiCalling: !data?.showApiCalling };
    } else if (name === 'summary') {
      object = { summary: value };
    } else {
      object = { generateSessionId: !data?.generateSessionId };
    }

    nodeDataAutoSaveDynamic({
      setNodeState,
      id,
      flowId,
      objectData: {
        ...object,
      },
      setSaving,
      setSchema,
      newEdges: edges,
    });

    setFormData(previousFormData => ({
      ...previousFormData,
      ...object,
    }));
  };

  const saveNode = useCallback(
    (data: any, changeType: string = 'default') => {
      nodeDataAutoSaveDynamic({
        setNodeState,
        id,
        flowId,
        objectData: {
          ...data,
        },
        setSaving,
        setSchema,
        newEdges: edges,
        changeType,
      });
    },
    [edges, flowId, id, setNodeState, setSaving, setSchema],
  );

  const onCancelExecute = () => {
    setNodeState(nds => {
      return nds.map((nd: any) => {
        return {
          ...nd,
          data: {
            ...nd.data,
            flowTrigger: null,
            canceled: true,
          },
        };
      });
    });

    nodes?.forEach((element: any) =>
      nodeDataAutoSaveDynamic({
        setNodeState,
        id: element.id,
        flowId,
        objectData: {
          usedTokens: undefined,
          hasExecutionError: false,
        },
        setSaving,
        setSchema,
        newEdges: edges,
      }),
    );
    setFormData(previousFormData => ({
      ...previousFormData,
      loading: false,
    }));
  };

  const onPauseOrUnpause = (pause: boolean) => {
    setNodeState(nds => {
      return nds.map((nd: any) => {
        return {
          ...nd,
          data: {
            ...nd.data,
            paused: pause,
          },
        };
      });
    });
  };

  const toggleNotes = () => {
    setFormData(previousFormData => ({
      ...previousFormData,
      notesExpanded: !previousFormData.notesExpanded,
    }));
  };

  const handleChange = async (e: any) => {
    const isTargetNameAutoTrigger = e.target.name === 'isAutotrigger';

    if (isTargetNameAutoTrigger && formData.isAutotrigger) {
      setIsAutoTriggerModalOpen(false);

      const previousFormData = { ...formData };
      const previousNodeData = { ...data };

      setFormData(prev => ({
        ...prev,
        [e.target.name]: e.target.checked,
        triggerMessage: '',
        intervalData: null,
        isAutotrigger: false,
      }));

      saveNode({
        ...data,
        isAutotrigger: false,
        triggerMessage: '',
        intervalData: null,
      });

      try {
        const res = await ScheduleService.getOneSchedule(flowId!);

        if (!res?.data?.id) return;

        await ScheduleService.deleteSchedule(res.data.id);

        return;
      } catch (error) {
        setFormData(previousFormData);
        saveNode(previousNodeData);
      }
    }

    if (isTargetNameAutoTrigger) {
      setIsAutoTriggerModalOpen(true);

      setFormData(prev => ({
        ...prev,
        [e.target.name]: e.target.checked,
      }));

      setNodeState(nds =>
        nds.map(nd => {
          if (nd.id !== id) return nd;

          return {
            ...nd,
            data: {
              ...nd.data,
              [e.target.name]: e.target.checked,
            },
          };
        }),
      );

      return;
    }

    setFormData(prev => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  const onChangeIsPublicOrWebhook = async (data: any) => {
    try {
      await FlowService.updateFlow(flowId!, {
        public: data.isPublic,
        webhook: data.webhook,
      });

      if ('isPublic' in data) {
        setIsPublic(data.isPublic);
      }
      if ('webhook' in data) {
        setIsWebhook(data.webhook);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!(data?.hasFinished && user && flowId)) return;
    if (logCreatedRef.current) return;

    logCreatedRef.current = true;

    const totalTokens = nodes.reduce((sum: any, item: any) => {
      if (typeof item?.data?.usedTokens === 'number') {
        return sum + item.data.usedTokens;
      }
      return sum;
    }, 0);

    const nodeWithError = nodes.find((item: any) => {
      if (item?.data?.hasExecutionError === true) return item?.data;
    });

    setNotification({
      type: 'success',
      message: 'Agent Global Finished',
    });

    const status = nodeWithError?.data.hasExecutionError
      ? 'Error while running'
      : 'Succesfully ran';
    const spentTokens = Math.round((totalTokens ?? 0) * 1000) / 1000;
    const cost = totalTokens * user.fluxTokenPrice * user.tokensPurchaseRate;

    createLogMutation.mutate({
      status,
      error_message: nodeWithError?.data.errorMessage || undefined,
      error_status_code: nodeWithError?.data.errorStatusCode?.toString() || undefined,
      spent_tokens: spentTokens,
      cost,
      run_type: 'manual',
      flowId,
    });

    const newLog = {
      date: new Date(),
      status,
      errorMessage: nodeWithError?.data.errorMessage
        ? nodeWithError?.data.errorMessage
        : null,
      errorStatusCode: nodeWithError?.data.errorStatusCode
        ? nodeWithError?.data.errorStatusCode.toString()
        : null,
      spentTokens,
      cost,
    };

    const existingLogs = Array.isArray(data?.logs) ? data.logs : [];
    const updatedLogs = [newLog, ...existingLogs];
    const limitedLogs =
      updatedLogs.length > 100 ? updatedLogs.slice(0, 100) : updatedLogs;

    saveNode(
      {
        logs: limitedLogs,
        flowUsedTokens: undefined,
      },
      'save-logs',
    );
  }, [data?.hasFinished, user, nodes, flowId, setNotification, saveNode]);

  useEffect(() => {
    if (!data?.hasFinished) {
      logCreatedRef.current = false;
    }
  }, [data?.hasFinished]);

  useEffect(() => {
    if (!(data?.hasFinished && user)) return;

    nodes?.forEach((element: any) =>
      nodeDataAutoSaveDynamic({
        setNodeState,
        id: element.id,
        flowId,
        objectData: {
          usedTokens: undefined,
          hasExecutionError: false,
          errorStatusCode: null,
          errorMessage: null,
        },
        setSaving,
        setSchema,
        newEdges: edges,
      }),
    );
  }, [
    data?.hasFinished,
    user,
    nodes,
    flowId,
    setNodeState,
    edges,
    setSaving,
    setSchema,
  ]);

  useEffect(() => {
    if (!(data?.hasFinished && user)) return;

    const onUpdateChat = async (data: any, fluxId: string) => {
      try {
        await FlowService.updateNodeData(flowId!, {
          id: fluxId,
          data: {
            ...data,
          },
          changeType: 'default',
        });
      } catch (error) {
        console.log(error);
      }
    };

    setFormData(previousFormData => ({
      ...previousFormData,
      loading: false,
    }));

    setNodeState(nds => {
      const activeFluxNode = nds.find(
        (nd: any) => nd.type === 'fluxObject' && nd?.data?.chat === true,
      );
      const inputNode = nds.find((nd: any) => nd.type === 'varInputNode');
      const outputNode = nds.find((nd: any) => nd.type === 'outputObjectNode');
      const combinedText = (outputNode?.data as any)?.textData
        ?.map((item: { text: string }) => item.text)
        ?.join(' ');

      return nds.map((nd: any) => {
        if (nd.type === 'outputObjectNode') {
          return updateOutputNode(nd);
        }

        if (nd.type === 'fluxObject' && activeFluxNode?.id === nd.id) {
          return updateFluxObjectNode(
            nd,
            activeSession,
            inputNode,
            combinedText,
            onUpdateChat,
          );
        }

        if (nd.id === id) {
          return updateCurrentNode(nd);
        }

        return updateDefaultNode(nd);
      });
    });
  }, [data?.hasFinished, user, setFormData, setNodeState, id, nodes, flowId]);

  useEffect(() => {
    if (data?.hasFinished && user) return;

    setFormData(previousFormData => ({
      ...previousFormData,
      notesExpanded: data.notesExpanded,
      notes: data.notes,
      triggerMessage: data.triggerMessage,
      isAutotrigger: data.isAutotrigger || false,
      generateSessionId: data?.generateSessionId || false,
      selectedNode: data?.selectedNode || 'none',
    }));
  }, [
    data?.generateSessionId,
    data?.hasFinished,
    data.isAutotrigger,
    data.notes,
    data.notesExpanded,
    data?.selectedNode,
    data.triggerMessage,
    user,
  ]);

  useEffect(() => {
    if (data?.loading) {
      setFormData(previousFormData => ({
        ...previousFormData,
        loading: true,
      }));
    }
  }, [data?.loading, formData]);

  const onCreateNewSession = (name: string) => {
    const newSession = createSession(name);

    setNotification({
      message: `Session "${name}" created successfully`,
      type: 'success',
    });

    nodeDataAutoSaveDynamic({
      setNodeState,
      id,
      flowId,
      objectData: {
        sessions: [...data.sessions, newSession],
      },
      setSaving,
      setSchema,
      newEdges: edges,
    });
  };

  return (
    <Box
      className={formData.loading ? 'block' : 'block-unactive'}
      sx={{
        bgcolor: user?.theme === 'dark' ? '#1D2939' : 'background.paper',
        p: '16px',
        borderRadius: '8px',
        width: '350px',
      }}
    >
      <Box
        display={{
          display: 'flex',
          justifyContent: 'end',
          gap: '8px',
        }}
      >
        <IconButtonTooltip title={'Open Notes'} onClick={toggleNotes}>
          <Sticker color="#667085" size={20} />
        </IconButtonTooltip>
        <IconButtonTooltip
          onClick={() => {
            setSettingsModalOpen(true);
          }}
          title={'Settings'}
        >
          <Gear color="#667085" size={20} />
        </IconButtonTooltip>
      </Box>
      <Box>
        <GlobeHemisphereEast weight="fill" size={32} color="#667085" />

        <Label
          isAdvanced
          labelName={data.label}
          sx={{
            mt: '11px',
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: '24px',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            mt: '8px',
          }}
        >
          <Typography
            sx={{
              color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
              fontSize: '12px',
              fontWeight: 700,
            }}
          >
            {allIds.length}{' '}
            <span
              style={{
                color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              Agent Steps
            </span>
          </Typography>
          <Typography
            sx={{
              color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
              fontSize: '12px',
              fontWeight: 700,
            }}
          >
            {reactFlow?.getNodes()?.length - 1}{' '}
            <span
              style={{
                color: user?.theme === 'dark' ? '#98A2B3' : '#667085',
                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              Nodes
            </span>
          </Typography>
        </Box>

        <Box mt={'20px'}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isAutotrigger}
                onChange={handleChange}
                name="isAutotrigger"
              />
            }
            label="Set Auto Trigger"
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#142BFF',
            }}
            componentsProps={{
              typography: {
                fontSize: '14px',
                alignSelf: 'center',
                marginTop: '2px',
              },
            }}
          />
          {formData.isAutotrigger && formData.triggerMessage && (
            <>
              <Typography
                style={{
                  color: '#475467',
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center',
                  fontSize: '12px',
                  marginBottom: '10px',
                }}
                onClick={() => setIsAutoTriggerModalOpen(true)}
              >
                <CalendarBlank />
                {formData.triggerMessage}

                {formData.loading && (
                  <Chip
                    label="Active"
                    sx={{ bgcolor: '#4CAF50', color: 'white' }}
                    size="small"
                  />
                )}
              </Typography>
            </>
          )}
        </Box>

        <Box>
          <Button
            variant="contained"
            onClick={executeFlow}
            fullWidth
            disabled={formData.loading}
          >
            Run Agent
          </Button>{' '}
          {formData.loading && (
            <Box>
              <Button
                variant="contained"
                onClick={() => {
                  onPauseOrUnpause(data?.paused ? false : true);
                }}
                sx={{
                  mt: '16px',
                }}
              >
                {data?.paused ? 'Unpause' : 'Pause'}
              </Button>
            </Box>
          )}
          {formData.loading && (
            <Button
              variant="contained"
              onClick={onCancelExecute}
              fullWidth
              sx={{
                mt: '16px',
              }}
            >
              Cancel Agent
            </Button>
          )}
        </Box>
        <Box
          sx={{
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid #EAECF0',
            pt: '10px',
          }}
        >
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              color: '#475467',
            }}
            onClick={() => setShowLogsModal(true)}
          >
            <Table size={16} />
            Agent Logs
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              color: '#4CAF50',
            }}
          >
            {logsData?.data?.[0]?.createdAt
              ? `${dayjs(logsData.data[0].createdAt).format('MMM D h:mm A')} - ${logsData.data[0].status}`
              : 'No logs yet'}
          </Typography>
        </Box>

        {formData.notesExpanded && (
          <Box mt={'16px'}>
            <textarea
              className="nowheel nodrag"
              id="notes"
              value={formData.notes}
              onChange={handleChange}
              name="notes"
              style={{
                height: 100,
                width: 'calc(100% - 16px)',
                padding: 8,
                fontFamily: 'inherit',
                borderRadius: '4px',
                outline: 'none',
                border: `1px solid ${
                  user?.theme === 'dark' ? '#344054' : '#D0D5DD'
                }`,
                backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#fff',
                color: user?.theme === 'dark' ? '#98A2B3' : 'black',
              }}
            />
          </Box>
        )}
      </Box>

      <OutputHandle
        colorActive={{
          background: "blue",
          color: "#3650DE",
        }}
        handleId={HandleId.Global}
        isActive={isActiveEdge(edges, id, HandleId.Global, 'source')}
      />
      <AutoTriggerTimerModal
        isAutoTriggerModalOpen={isAutoTriggerModalOpen}
        setIsAutoTriggerModalOpen={setIsAutoTriggerModalOpen}
        formData={formData}
        setFormData={setFormData}
        saveNode={saveNode}
        intervalData={intervalData}
        setIntervalData={setIntervalData}
        activeFlowId={activeFlow?.id}
      />

      <SettingsModal
        formData={formData}
        onChange={onChange}
        isPublic={isPublic}
        isWebhook={isWebhook}
        onChangeIsPublicOrWebhook={onChangeIsPublicOrWebhook}
        onHide={() => {
          setSettingsModalOpen(false);
        }}
        spaceId={spaceId}
        flowId={flowId}
        show={settingsModalOpen}
        onCreateNewSession={onCreateNewSession}
      />

      <KitModal
        show={showLogsModal}
        onHide={() => setShowLogsModal(false)}
        fullWidth
        sx={{
          '& .MuiDialogContent-root': {
            overflow: 'hidden',
            padding: 0,
          },
        }}
        title={
          <Typography
            sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <GlobeHemisphereEast weight="fill" size={32} color="#667085" />
            <Label
              isAdvanced
              labelName={data.label + ' Logs'}
              sx={{
                mt: '11px',
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: '24px',
              }}
            />
          </Typography>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '70vh', p: 3 }}>
          <TableContainer
            component={Paper}
            sx={{ 
              boxShadow: 'none', 
              flex: 1, 
              overflow: 'auto',
              minHeight: 0
            }}
          >
            <TableUI>
              <TableHead sx={{ bgcolor: '#EAECF0', py: '16px', position: 'sticky', top: 0, zIndex: 1 }}>
                <TableRow
                  sx={{
                    py: '6px',
                  }}
                >
                  <TableCell>Date and Time</TableCell>
                  <TableCell>Run Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logsLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : logsData?.data && logsData.data.length > 0 ? (
                  logsData.data.map((row: any, index: any) => (
                    <TableRow key={row.id || index}>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontWeight: 500,
                            color: '#007AFF',
                          }}
                        >
                          {dayjs(row.createdAt).format('h:mm A')}
                        </Typography>
                        <Typography
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            mt: '4px',
                            fontSize: '14px',
                            color: '#475467',
                          }}
                        >
                          <CalendarBlank size={16} />
                          {dayjs(row.createdAt).format('MM/DD/YYYY')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.run_type || 'Manual'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.status && (
                          <Typography variant="body2">
                            {row.status}
                            {row.error_status_code && ` - ${row.error_status_code}`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography>{row.spent_tokens}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>
                          {row.cost && typeof row.cost === 'number'
                            ? `$${row.cost.toFixed(6)}`
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.status === 'Error while running' && (
                          <Button
                            size="small"
                            variant="text"
                            color="error"
                            onClick={() => {
                              alert(
                                `Error message: ${row.error_message} - Status code: ${row.error_status_code}`,
                              );
                            }}
                          >
                            <Info fontSize="small" /> Error info
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography sx={{ py: 3, color: '#98A2B3' }}>
                        No logs yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </TableUI>
          </TableContainer>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 2,
              pt: 2,
              borderTop: '1px solid #EAECF0',
              flexShrink: 0
            }}
          >
            <Typography sx={{ fontSize: '14px', color: '#475467' }}>
              Total: {logsData?.total || 0} logs
            </Typography>
            {logsData && logsData.totalPages > 1 && (
              <Pagination
                count={logsData.totalPages}
                page={logsPage}
                onChange={(_, page) => setLogsPage(page)}
                color="primary"
              />
            )}
          </Box>
        </Box>
      </KitModal>
    </Box>
  );
}

export default memo(FlowGlobalNode);
